/**
 * Designer invoice review — Firestore subscribe + staff approve/reject API.
 * Mirrors materialsPriceUpdateService patterns.
 */

import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  type Unsubscribe,
  type FirestoreError,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { db, auth, storage } from './firebase';
import { DESIGNER_INVOICE_AGENT_ENDPOINT } from './designerInvoiceAgentGuide';
import {
  evaluateClientApproveGates,
  formatInvoiceMoney,
  newEmptyLine,
  withResolvedHubProjects,
  resolveDesignerInvoiceHubProject,
  type DesignerInvoiceLine,
  type DesignerInvoicePhase,
  type DesignerInvoiceBucket,
  type DesignerInvoiceOutcome,
  type ApproveGateIssue,
} from './designerInvoiceGates';

export type {
  DesignerInvoiceLine,
  DesignerInvoicePhase,
  DesignerInvoiceBucket,
  DesignerInvoiceOutcome,
  ApproveGateIssue,
};
export {
  evaluateClientApproveGates,
  formatInvoiceMoney,
  newEmptyLine,
  withResolvedHubProjects,
  resolveDesignerInvoiceHubProject,
};

export type DesignerInvoiceStatus =
  | 'pending_review'
  | 'pending_approval'
  | 'approved'
  | 'rejected';

export interface InvoiceReviewApprovalStep {
  gate: string;
  roleLabel: string;
  userId?: string | null;
  userName?: string | null;
  status: 'pending' | 'approved' | 'skipped' | 'rejected';
  decidedAt?: string | null;
  note?: string | null;
}

export interface DesignerInvoiceDoc {
  id: string;
  vendor: string;
  invoiceNumber: string;
  invoiceDate?: string | null;
  dueDate?: string | null;
  invoiceTotal: number;
  status: DesignerInvoiceStatus;
  source?: string | null;
  storagePath?: string | null;
  downloadUrl?: string | null;
  fileName?: string | null;
  contentType?: string | null;
  parseNotes?: string | null;
  forLabel?: string | null;
  suggestedJobLabel?: string | null;
  suggestedPhase?: DesignerInvoicePhase | null;
  suggestedAccountId?: string | null;
  suggestedAccountName?: string | null;
  reviewDealId?: string | null;
  reviewProjectId?: string | null;
  reviewJobLabel?: string | null;
  reviewPhase?: DesignerInvoicePhase | null;
  reviewAccountId?: string | null;
  reviewAccountName?: string | null;
  approvalBandId?: string | null;
  approvalBandLabel?: string | null;
  approvalSteps?: InvoiceReviewApprovalStep[];
  /**
   * project_card | company_card | personal_card | ach_office.
   * Default: estimating/precon/overhead under $2k → project_card; else ach_office.
   */
  paymentMethodId?: string | null;
  proposedLines: DesignerInvoiceLine[];
  /** Set on approve — preferred source for Project P&L job-direct cash. */
  allocations?: DesignerInvoiceLine[];
  /** Job POs issued on FINAL approve only. */
  purchaseRequisitionIds?: string[];
  poNumbers?: string[];
  badgesVisible?: boolean;
  /** Set when approve created the QuickBooks vendor bill (qbExpenses mirror doc id / QBO Bill Id). */
  qbExpenseId?: string | null;
  qboBillId?: string | null;
  approvedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export const DESIGNER_INVOICES_INDEX_HINT =
  'Firestore composite index on designerInvoices: status ASC, createdAt DESC';

const COLLECTION = 'designerInvoices';

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof v === 'object' && v && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function asLine(raw: unknown, index: number): DesignerInvoiceLine {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    id: typeof r.id === 'string' && r.id.trim() ? r.id.trim() : `line_${index + 1}`,
    description: typeof r.description === 'string' ? r.description : null,
    amount: typeof r.amount === 'number' && Number.isFinite(r.amount) ? r.amount : 0,
    hours: typeof r.hours === 'number' && Number.isFinite(r.hours) ? r.hours : null,
    rate: typeof r.rate === 'number' && Number.isFinite(r.rate) ? r.rate : null,
    dealId: typeof r.dealId === 'string' ? r.dealId : null,
    projectId: typeof r.projectId === 'string' ? r.projectId : null,
    dealLabel: typeof r.dealLabel === 'string' ? r.dealLabel : null,
    projectLabel: typeof r.projectLabel === 'string' ? r.projectLabel : null,
    phase: (['estimating', 'precon', 'under_contract', 'overhead'].includes(String(r.phase))
      ? String(r.phase)
      : 'estimating') as DesignerInvoicePhase,
    bucket: (['external_design', 'external_engineering', 'training_onboarding', 'other'].includes(
      String(r.bucket || 'external_design'),
    )
      ? String(r.bucket || 'external_design')
      : 'external_design') as DesignerInvoiceBucket,
    outcome: (['open', 'won', 'lost'].includes(String(r.outcome || ''))
      ? String(r.outcome)
      : null) as DesignerInvoiceOutcome | null,
  };
}

function parseIdList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const value of raw) {
    if (typeof value !== 'string') continue;
    const id = value.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids.length ? ids : undefined;
}

function asPhase(raw: unknown): DesignerInvoicePhase | null {
  const s = String(raw || '');
  return (['estimating', 'precon', 'under_contract', 'overhead'].includes(s)
    ? s
    : null) as DesignerInvoicePhase | null;
}

function asApprovalSteps(raw: unknown): InvoiceReviewApprovalStep[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  return raw
    .filter((row) => row && typeof row === 'object')
    .map((row) => {
      const r = row as Record<string, unknown>;
      return {
        gate: String(r.gate || ''),
        roleLabel: String(r.roleLabel || r.gate || 'Approver'),
        userId: typeof r.userId === 'string' ? r.userId : null,
        userName: typeof r.userName === 'string' ? r.userName : null,
        status: (['pending', 'approved', 'skipped', 'rejected'].includes(String(r.status))
          ? String(r.status)
          : 'pending') as InvoiceReviewApprovalStep['status'],
        decidedAt: typeof r.decidedAt === 'string' ? r.decidedAt : null,
        note: typeof r.note === 'string' ? r.note : null,
      };
    });
}

function fromDoc(id: string, d: Record<string, unknown>): DesignerInvoiceDoc {
  const lines = Array.isArray(d.proposedLines) ? d.proposedLines.map(asLine) : [];
  const allocations = Array.isArray(d.allocations) ? d.allocations.map(asLine) : undefined;
  return {
    id,
    vendor: String(d.vendor || ''),
    invoiceNumber: String(d.invoiceNumber || ''),
    invoiceDate: typeof d.invoiceDate === 'string' ? d.invoiceDate : null,
    dueDate: typeof d.dueDate === 'string' ? d.dueDate : null,
    invoiceTotal: typeof d.invoiceTotal === 'number' ? d.invoiceTotal : 0,
    status: (d.status as DesignerInvoiceStatus) || 'pending_review',
    source: typeof d.source === 'string' ? d.source : null,
    storagePath: typeof d.storagePath === 'string' ? d.storagePath : null,
    downloadUrl:
      typeof d.downloadUrl === 'string'
        ? d.downloadUrl
        : typeof d.fileUrl === 'string'
          ? d.fileUrl
          : typeof d.pdfUrl === 'string'
            ? d.pdfUrl
            : null,
    fileName: typeof d.fileName === 'string' ? d.fileName : null,
    contentType: typeof d.contentType === 'string' ? d.contentType : null,
    parseNotes: typeof d.parseNotes === 'string' ? d.parseNotes : null,
    forLabel: typeof d.forLabel === 'string' ? d.forLabel : null,
    suggestedJobLabel: typeof d.suggestedJobLabel === 'string' ? d.suggestedJobLabel : null,
    suggestedPhase: asPhase(d.suggestedPhase),
    suggestedAccountId: typeof d.suggestedAccountId === 'string' ? d.suggestedAccountId : null,
    suggestedAccountName: typeof d.suggestedAccountName === 'string' ? d.suggestedAccountName : null,
    reviewDealId: typeof d.reviewDealId === 'string' ? d.reviewDealId : null,
    reviewProjectId: typeof d.reviewProjectId === 'string' ? d.reviewProjectId : null,
    reviewJobLabel: typeof d.reviewJobLabel === 'string' ? d.reviewJobLabel : null,
    reviewPhase: asPhase(d.reviewPhase),
    reviewAccountId: typeof d.reviewAccountId === 'string' ? d.reviewAccountId : null,
    reviewAccountName: typeof d.reviewAccountName === 'string' ? d.reviewAccountName : null,
    approvalBandId: typeof d.approvalBandId === 'string' ? d.approvalBandId : null,
    approvalBandLabel: typeof d.approvalBandLabel === 'string' ? d.approvalBandLabel : null,
    approvalSteps: asApprovalSteps(d.approvalSteps),
    paymentMethodId: typeof d.paymentMethodId === 'string' && d.paymentMethodId.trim()
      ? d.paymentMethodId.trim()
      : null,
    proposedLines: lines,
    allocations,
    purchaseRequisitionIds: parseIdList(d.purchaseRequisitionIds),
    poNumbers: parseIdList(d.poNumbers),
    badgesVisible: d.badgesVisible === true,
    qbExpenseId: typeof d.qbExpenseId === 'string' && d.qbExpenseId.trim() ? d.qbExpenseId.trim() : null,
    qboBillId: typeof d.qboBillId === 'string' && d.qboBillId.trim() ? d.qboBillId.trim() : null,
    approvedAt: toDate(d.approvedAt),
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

function sortNewestFirst(list: DesignerInvoiceDoc[]): DesignerInvoiceDoc[] {
  return [...list].sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
}

function isMissingIndexError(err: FirestoreError | Error): boolean {
  const code = 'code' in err ? String(err.code || '') : '';
  const msg = err.message || '';
  return (
    code === 'failed-precondition' ||
    /requires an index|FAILED_PRECONDITION|failed-precondition/i.test(msg)
  );
}

export type DesignerInvoiceListener = (
  list: DesignerInvoiceDoc[],
  meta?: { error?: string | null; usedFallback?: boolean },
) => void;

const OPEN_INVOICE_STATUSES: DesignerInvoiceStatus[] = ['pending_review', 'pending_approval'];

export function subscribePendingDesignerInvoices(callback: DesignerInvoiceListener): Unsubscribe {
  // Include pending_approval so later gates (e.g. executive after supervisor) stay in queue.
  const preferred = query(
    collection(db, COLLECTION),
    where('status', 'in', OPEN_INVOICE_STATUSES),
    orderBy('createdAt', 'desc'),
  );

  let fallbackUnsub: Unsubscribe | null = null;
  let cancelled = false;

  const preferredUnsub = onSnapshot(
    preferred,
    (snap) => {
      callback(
        snap.docs.map((d) => fromDoc(d.id, d.data() as Record<string, unknown>)),
        { error: null, usedFallback: false },
      );
    },
    (err) => {
      console.error('[designerInvoices] preferred query failed:', err.message);
      if (cancelled) return;
      if (isMissingIndexError(err)) {
        const fallback = query(
          collection(db, COLLECTION),
          where('status', 'in', OPEN_INVOICE_STATUSES),
        );
        fallbackUnsub = onSnapshot(
          fallback,
          (snap) => {
            callback(
              sortNewestFirst(snap.docs.map((d) => fromDoc(d.id, d.data() as Record<string, unknown>))),
              { error: null, usedFallback: true },
            );
          },
          (fallbackErr) => {
            callback([], {
              error: fallbackErr.message || 'Failed to load designer invoices',
              usedFallback: true,
            });
          },
        );
        return;
      }
      callback([], { error: err.message || 'Failed to load designer invoices', usedFallback: false });
    },
  );

  return () => {
    cancelled = true;
    preferredUnsub();
    fallbackUnsub?.();
  };
}

export async function listApprovedDesignerInvoices(): Promise<DesignerInvoiceDoc[]> {
  const snap = await getDocs(query(collection(db, COLLECTION), where('status', '==', 'approved')));
  return snap.docs.map((d) => fromDoc(d.id, d.data() as Record<string, unknown>));
}

export function subscribeApprovedDesignerInvoices(callback: DesignerInvoiceListener): Unsubscribe {
  const preferred = query(
    collection(db, COLLECTION),
    where('status', '==', 'approved'),
    orderBy('approvedAt', 'desc'),
  );

  let fallbackUnsub: Unsubscribe | null = null;
  let cancelled = false;

  const preferredUnsub = onSnapshot(
    preferred,
    (snap) => {
      callback(
        snap.docs.map((d) => fromDoc(d.id, d.data() as Record<string, unknown>)),
        { error: null, usedFallback: false },
      );
    },
    (err) => {
      console.error('[designerInvoices] approved query failed:', err.message);
      if (cancelled) return;
      if (isMissingIndexError(err)) {
        const fallback = query(collection(db, COLLECTION), where('status', '==', 'approved'));
        fallbackUnsub = onSnapshot(
          fallback,
          (snap) => {
            callback(
              sortNewestFirst(snap.docs.map((d) => fromDoc(d.id, d.data() as Record<string, unknown>))),
              { error: null, usedFallback: true },
            );
          },
          (fallbackErr) => {
            callback([], {
              error: fallbackErr.message || 'Failed to load approved designer invoices',
              usedFallback: true,
            });
          },
        );
        return;
      }
      callback([], { error: err.message || 'Failed to load approved designer invoices', usedFallback: false });
    },
  );

  return () => {
    cancelled = true;
    preferredUnsub();
    fallbackUnsub?.();
  };
}

async function getStaffIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in required to review designer invoices');
  }
  return user.getIdToken();
}

async function staffAction(
  action:
    | 'getDesignerInvoicePdf'
    | 'updateDesignerInvoiceReview'
    | 'approveDesignerInvoice'
    | 'rejectDesignerInvoice'
    | 'tryCreateDesignerInvoiceQboBill',
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const token = await getStaffIdToken();
  const response = await fetch(DESIGNER_INVOICE_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action, ...body }),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok || data?.success === false) {
    const issues = Array.isArray(data.issues) ? data.issues : null;
    const issueMsg =
      issues && issues.length
        ? issues.map((i: { message?: string }) => i.message || '').filter(Boolean).join('; ')
        : null;
    throw new Error(
      issueMsg ||
        (typeof data?.error === 'string' && data.error) ||
        `Designer invoice ${action} failed (HTTP ${response.status})`,
    );
  }
  return data;
}

export async function saveDesignerInvoiceReview(
  invoiceId: string,
  lines: DesignerInvoiceLine[],
  invoiceTotal?: number,
): Promise<void> {
  await staffAction('updateDesignerInvoiceReview', {
    invoiceId,
    lines,
    ...(invoiceTotal != null ? { invoiceTotal } : {}),
  });
}

/** Update job / account / phase on the simple invoice review page. */
export async function saveInvoiceReviewFields(
  invoiceId: string,
  fields: {
    reviewDealId?: string | null;
    reviewProjectId?: string | null;
    reviewJobLabel?: string | null;
    reviewPhase?: DesignerInvoicePhase | null;
    reviewAccountId?: string | null;
    reviewAccountName?: string | null;
    dueDate?: string | null;
    paymentMethodId?: string | null;
  },
): Promise<void> {
  await staffAction('updateDesignerInvoiceReview', {
    invoiceId,
    ...fields,
  });
}

export async function saveDesignerInvoicePaymentMethod(
  invoiceId: string,
  paymentMethodId: string,
): Promise<void> {
  await staffAction('updateDesignerInvoiceReview', {
    invoiceId,
    paymentMethodId,
  });
}

/** Pending mapping review only (banner count). */
export function subscribePendingReviewInvoices(callback: DesignerInvoiceListener): Unsubscribe {
  return subscribePendingDesignerInvoices(callback);
}

export async function approveDesignerInvoice(
  invoiceId: string,
  lines: DesignerInvoiceLine[],
  extras?: {
    purchaseRequisitionIds?: string[];
    poNumbers?: string[];
    paymentMethodId?: string | null;
  },
): Promise<Record<string, unknown>> {
  return staffAction('approveDesignerInvoice', {
    invoiceId,
    lines,
    ...(extras?.purchaseRequisitionIds?.length
      ? { purchaseRequisitionIds: extras.purchaseRequisitionIds }
      : {}),
    ...(extras?.poNumbers?.length ? { poNumbers: extras.poNumbers } : {}),
    ...(extras?.paymentMethodId ? { paymentMethodId: extras.paymentMethodId } : {}),
  });
}

export async function tryCreateDesignerInvoiceQboBills(args: {
  invoiceId: string;
  purchaseRequisitionIds: string[];
}): Promise<Record<string, unknown>> {
  return staffAction('tryCreateDesignerInvoiceQboBill', args);
}

export async function rejectDesignerInvoice(invoiceId: string, reason?: string): Promise<void> {
  await staffAction('rejectDesignerInvoice', { invoiceId, reason });
}

export async function resolveDesignerInvoicePdfUrl(
  invoice: DesignerInvoiceDoc,
): Promise<string | null> {
  if (invoice.downloadUrl) return invoice.downloadUrl;
  if (invoice.storagePath) {
    try {
      return await getDownloadURL(storageRef(storage, invoice.storagePath));
    } catch (err) {
      console.warn('[designerInvoices] getDownloadURL failed', invoice.storagePath, err);
    }
  }
  try {
    const data = await staffAction('getDesignerInvoicePdf', { invoiceId: invoice.id });
    if (typeof data.pdfUrl === 'string' && data.pdfUrl.trim()) return data.pdfUrl.trim();
    const nested = data.invoice && typeof data.invoice === 'object'
      ? (data.invoice as Record<string, unknown>)
      : null;
    if (nested && typeof nested.downloadUrl === 'string' && nested.downloadUrl.trim()) {
      return nested.downloadUrl.trim();
    }
  } catch (err) {
    console.warn('[designerInvoices] getDesignerInvoicePdf fallback failed', invoice.id, err);
  }
  return null;
}
